// ============================================================================
// keresesView.js — a keresés felülete (2026-10-04, tagi kérésre).
//
// Önálló, a chat-képernyő kirajzolásától FÜGGETLEN réteg (mint a kepNezo.js):
// a <body>-ra kerül, saját állapota van, így a chat újrarajzolásai (új
// üzenet érkezik, miközben keresel) nem zavarják meg, és a render-
// gyorsítótárhoz (chatView sorAlairas) sem kell hozzányúlni.
//
// A keresés LOGIKÁJA a kereso.js-ben van (tesztelhető, DOM nélkül). Itt csak
// a bemenet, az állapotsor és a találati lista él.
// ============================================================================

import { t, localeFor } from "./i18n.js";
import { feladoNev, getRoomDisplayName } from "./chatView.js";
import { keresesiSzavak, ujKereses, reszlet, MIN_KARAKTER } from "./kereso.js";
import { log } from "./log.js";

const KESLELTETES_MS = 350;
const TALALAT_LAPONKENT = 30;
const MAX_LAP_KORONKENT = 40; // 40 lap = 2000 üzenet / lépés

let nyitott = null;
// A találatra ugrás után a keresés állapota (kifejezés, hatókör, találatok, a folytatható motor, görgetés) itt marad, hogy a
// "Vissza a keresési eredményekhez" gombbal ugyanoda lehessen visszatérni (2026-10-24, a titkár #79–#80 javaslata).
let megorzott = null;
let visszaSav = null;

export function keresoNyitva() {
  return nyitott !== null;
}

export function zarjBeKeresot() {
  if (nyitott) nyitott.bezar();
}

export function elrejtVisszaSavot() {
  if (visszaSav) { visszaSav.remove(); visszaSav = null; }
}

export function felejtKeresest() {
  megorzott = null;
  elrejtVisszaSavot();
}

export function visszaSavVan() {
  return visszaSav !== null;
}

function mutassVisszaSavot(params) {
  elrejtVisszaSavot();
  const sav = document.createElement("div");
  sav.className = "kereso-vissza";
  const gomb = document.createElement("button");
  gomb.type = "button";
  gomb.className = "kereso-vissza-gomb";
  gomb.textContent = t(params.uiLang, "search.back");
  gomb.addEventListener("click", () => nyissKeresot({ ...params, visszaallit: megorzott }));
  const x = document.createElement("button");
  x.type = "button";
  x.className = "kereso-vissza-x";
  x.textContent = "×";
  x.setAttribute("aria-label", t(params.uiLang, "search.close"));
  x.addEventListener("click", () => { megorzott = null; elrejtVisszaSavot(); });
  sav.appendChild(gomb);
  sav.appendChild(x);
  document.body.appendChild(sav);
  visszaSav = sav;
}

function datum(ms, uiLang) {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleString(localeFor(uiLang), { dateStyle: "short", timeStyle: "short" });
  } catch (e) {
    return new Date(ms).toISOString().slice(0, 16).replace("T", " ");
  }
}

/**
 * @param {object} p
 * @param {string} p.uiLang
 * @param {string} p.tartalomNyelv
 * @param {Array<object>} p.szobak        a felhasználó szobái ({roomId, name, ...})
 * @param {string|null} p.aktualisSzobaId
 * @param {(roomId: string, kurzor: any) => Promise<any>} p.lapLekeres
 * @param {(roomId: string, messageId: string) => void} p.onJump
 * @param {(roomId: string, uzenet: object) => string} [p.temaNev]   a találat témájának neve (ha ismert; különben üres)
 * @param {object} [p.visszaallit]   a megőrzött állapot (a "Vissza a keresési eredményekhez" gombról)
 */
export function nyissKeresot(params) {
  const { uiLang, tartalomNyelv, szobak, aktualisSzobaId, lapLekeres, onJump, temaNev } = params;
  zarjBeKeresot();
  elrejtVisszaSavot();

  const aktualis = (szobak || []).find((s) => s.roomId === aktualisSzobaId) || null;
  let hatokor = aktualis ? "szoba" : "mind"; // "szoba" | "mind"
  let futasId = 0;
  let motor = null;
  let talalatok = [];
  let idozito = null;
  let utolsoKesz = false;
  let utolsoAllapot = { kulcs: "", befejezetlen: false };

  const gyoker = document.createElement("div");
  gyoker.className = "kereso";
  gyoker.setAttribute("role", "dialog");

  // --- Fej: bemenet + bezárás
  const fej = document.createElement("div");
  fej.className = "kereso-fej";
  const bemenet = document.createElement("input");
  bemenet.type = "search";
  bemenet.className = "kereso-bemenet";
  bemenet.placeholder = t(uiLang, "search.placeholder");
  bemenet.setAttribute("aria-label", t(uiLang, "search.placeholder"));
  bemenet.autocomplete = "off";
  const bezarGomb = document.createElement("button");
  bezarGomb.type = "button";
  bezarGomb.className = "kereso-bezar";
  bezarGomb.textContent = t(uiLang, "search.close");
  const frissitGomb = document.createElement("button");
  frissitGomb.type = "button";
  frissitGomb.className = "kereso-frissit";
  frissitGomb.textContent = t(uiLang, "search.refresh");
  fej.appendChild(bemenet);
  fej.appendChild(frissitGomb);
  fej.appendChild(bezarGomb);
  gyoker.appendChild(fej);

  // --- Hatókör: csak akkor van értelme, ha több szoba is van
  const hatokorSor = document.createElement("div");
  hatokorSor.className = "kereso-hatokor";
  const gombok = {};
  for (const [kulcs, felirat] of [["szoba", "search.scopeRoom"], ["mind", "search.scopeAll"]]) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "kereso-hatokor-gomb";
    b.dataset.hatokor = kulcs;
    b.textContent = t(uiLang, felirat);
    b.addEventListener("click", () => { hatokor = kulcs; frissitHatokor(); indit(); });
    gombok[kulcs] = b;
    hatokorSor.appendChild(b);
  }
  if (aktualis && (szobak || []).length > 1) gyoker.appendChild(hatokorSor);

  const allapotsor = document.createElement("div");
  allapotsor.className = "kereso-allapot";
  gyoker.appendChild(allapotsor);

  const lista = document.createElement("div");
  lista.className = "kereso-lista";
  gyoker.appendChild(lista);

  const tovabb = document.createElement("button");
  tovabb.type = "button";
  tovabb.className = "kereso-tovabb";
  tovabb.textContent = t(uiLang, "search.more");
  tovabb.hidden = true;
  gyoker.appendChild(tovabb);

  function frissitHatokor() {
    for (const [k, b] of Object.entries(gombok)) b.classList.toggle("aktiv", k === hatokor);
  }

  // A "nincs találat" és a "még nem néztem át mindent" két külön állapot (2026-10-18, AI-titkári audit): kutatásban nagy a különbség
  // aközött, hogy "az eddig átnézettekben nincs" és hogy "mindent átnéztem, nincs". Az utóbbi az egyetlen, ami zárt állítás.
  function allapot(kulcs, n, befejezetlen = false) {
    // A "megvizsgált üzenetek" és a "találatok" külön szám: az előbbi nem az "ellenőrzött" (hitelesített) üzenetek száma.
    utolsoAllapot = { kulcs: kulcs || "", befejezetlen: befejezetlen === true };
    const szoveg = kulcs ? t(uiLang, kulcs, { n: n === undefined ? 0 : n }) : "";
    allapotsor.textContent = szoveg && talalatok.length > 0 ? `${t(uiLang, "search.found", { m: talalatok.length })} · ${szoveg}` : szoveg;
    allapotsor.classList.toggle("kereso-befejezetlen", befejezetlen === true);
    tovabb.classList.toggle("kereso-kiemelt", befejezetlen === true);
  }

  function kirajzol() {
    lista.textContent = "";
    const szavak = keresesiSzavak(bemenet.value);
    const rendezett = [...talalatok].sort((a, b) => b.ido - a.ido);
    for (const tal of rendezett) {
      const elem = document.createElement("button");
      elem.type = "button";
      elem.className = "kereso-talalat";
      elem.dataset.messageId = tal.uzenet.messageId;
      elem.dataset.roomId = tal.szoba.roomId;

      const meta = document.createElement("div");
      meta.className = "kereso-meta";
      const reszek = [feladoNev(tal.uzenet, tartalomNyelv, uiLang), datum(tal.ido, uiLang)];
      if (hatokor === "mind") reszek.push(getRoomDisplayName(tal.szoba, tartalomNyelv, uiLang));
      if (typeof temaNev === "function") {
        let tn = "";
        try { tn = temaNev(tal.szoba.roomId, tal.uzenet) || ""; } catch (e) { tn = ""; }
        if (tn) reszek.push(tn);
      }
      meta.textContent = reszek.filter(Boolean).join(" · ");
      elem.appendChild(meta);

      // A részlet MINDIG DOM-csomópontokból épül (textContent / <mark>), soha nem innerHTML-ből:
      // az üzenet szövege bármi lehet.
      const rl = reszlet(tal.szoveg, szavak);
      const sz = document.createElement("div");
      sz.className = "kereso-reszlet";
      if (rl.elol) sz.appendChild(document.createTextNode("… "));
      for (const r of rl.reszek) {
        if (r.kiemelt) {
          const m = document.createElement("mark");
          m.textContent = r.szoveg;
          sz.appendChild(m);
        } else {
          sz.appendChild(document.createTextNode(r.szoveg));
        }
      }
      if (rl.vegen) sz.appendChild(document.createTextNode(" …"));
      elem.appendChild(sz);

      elem.addEventListener("click", () => {
        const roomId = tal.szoba.roomId, messageId = tal.uzenet.messageId;
        megorzott = {
          szoveg: bemenet.value, hatokor, aktualisSzobaId, motor, talalatok: [...talalatok], kesz: utolsoKesz,
          gorgetes: lista.scrollTop, allapotKulcs: utolsoAllapot.kulcs, allapotBefejezetlen: utolsoAllapot.befejezetlen,
        };
        bezar();
        mutassVisszaSavot(params);
        onJump(roomId, messageId);
      });
      lista.appendChild(elem);
    }
  }

  async function futtat() {
    if (!motor) return;
    const sajat = futasId;
    tovabb.hidden = true;
    allapot("search.scanning", motor.vizsgalt);
    try {
      const v = await motor.folytat({
        maxTalalat: TALALAT_LAPONKENT,
        maxLap: MAX_LAP_KORONKENT,
        megszakitva: () => sajat !== futasId,
        ontalalat: (tal) => { if (sajat === futasId) talalatok.push(tal); },
        onhalad: ({ vizsgalt }) => {
          if (sajat !== futasId) return;
          allapot("search.scanning", vizsgalt);
          kirajzol();
        },
      });
      if (sajat !== futasId) return; // közben új keresés indult
      utolsoKesz = v.kesz === true;
      kirajzol();
      if (talalatok.length === 0 && v.kesz) allapot("search.noResults", motor.vizsgalt);
      else if (talalatok.length === 0) allapot("search.noneYet", motor.vizsgalt, true);
      else allapot(v.kesz ? "search.done" : "search.paused", motor.vizsgalt, !v.kesz);
      tovabb.hidden = v.kesz;
    } catch (err) {
      if (sajat !== futasId) return;
      log.error("[keresés] hiba:", err);
      allapotsor.textContent = t(uiLang, "search.error");
    }
  }

  function indit() {
    clearTimeout(idozito);
    futasId++;
    talalatok = [];
    motor = null;
    utolsoKesz = false;
    megorzott = null;
    lista.textContent = "";
    tovabb.hidden = true;
    const szavak = keresesiSzavak(bemenet.value);
    if (szavak.length === 0) {
      allapotsor.textContent = bemenet.value.trim() ? t(uiLang, "search.minChars", { n: MIN_KARAKTER }) : "";
      return;
    }
    const celszobak = hatokor === "szoba" && aktualis ? [aktualis] : (szobak || []);
    motor = ujKereses({ szobak: celszobak, lapLekeres, szavak, tartalomNyelv });
    futtat();
  }

  function bezar() {
    futasId++; // a futó keresés megszakad
    clearTimeout(idozito);
    document.removeEventListener("keydown", billentyu);
    gyoker.remove();
    if (nyitott && nyitott.gyoker === gyoker) nyitott = null;
  }

  function billentyu(e) {
    if (e.key === "Escape") bezar();
  }

  bemenet.addEventListener("input", () => {
    clearTimeout(idozito);
    idozito = setTimeout(indit, KESLELTETES_MS);
  });
  bemenet.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); indit(); }
  });
  bezarGomb.addEventListener("click", bezar);
  frissitGomb.addEventListener("click", () => { if (bemenet.value.trim()) indit(); });
  tovabb.addEventListener("click", futtat);
  document.addEventListener("keydown", billentyu);

  frissitHatokor();
  document.body.appendChild(gyoker);

  // Visszatérés a megőrzött keresésre: nem indul újra, a találatok és a folytatható motor ugyanazok; a "Frissítés" gomb újra keres.
  const v = params.visszaallit;
  if (v && v.motor && v.szoveg) {
    bemenet.value = v.szoveg;
    hatokor = v.hatokor === "mind" || !aktualis ? "mind" : "szoba";
    frissitHatokor();
    motor = v.motor;
    talalatok = [...v.talalatok];
    utolsoKesz = v.kesz === true;
    kirajzol();
    if (v.allapotKulcs) allapot(v.allapotKulcs, motor.vizsgalt, v.allapotBefejezetlen === true);
    tovabb.hidden = utolsoKesz;
    lista.scrollTop = v.gorgetes || 0;
  }
  bemenet.focus();

  nyitott = { gyoker, bezar };
  return { bezar, gyoker };
}
